import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Modal, Field, TextInput, Select, Textarea, FormActions, dbErrorMessage } from './form';

type BaseProps<T> = { open: boolean; onClose: () => void; onSaved: () => void; initial?: T | null };

export const TOOL_STATUSES = ['available', 'in_use', 'maintenance', 'retired', 'lost'] as const;
export type ToolStatus = (typeof TOOL_STATUSES)[number];

export type CategoryRow = { id: string; kind: 'part' | 'tool'; code: string; name: string; parent_id: string | null };
export type LocationRow = { id: string; code: string; name: string; notes: string | null };
export type ToolRow = {
  id: string; name: string; serial_number: string | null; category_id: string | null; location_id: string | null;
  supplier_id: string | null; purchase_date: string; purchase_price: number | null; currency: string;
  warranty_until: string | null; next_calibration_at: string | null; status: ToolStatus; notes: string | null;
  qr_token?: string;
};

// ── Parametrable library: category ───────────────────────────────────────────
export function CategoryForm({ open, onClose, onSaved, initial, kind }: BaseProps<CategoryRow> & { kind: 'part' | 'tool' }) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [f, setF] = useState({ code: '', name: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) return; setErr({}); setF({ code: initial?.code ?? '', name: initial?.name ?? '' }); }, [open, initial]);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.code.trim()) er.code = t('form.errRequired');
    if (!f.name.trim()) er.name = t('form.errRequired');
    setErr(er); if (Object.keys(er).length) return;
    setBusy(true);
    const payload = { code: f.code.trim(), name: f.name.trim(), kind };
    const res = initial?.id
      ? await supabase.from('inventory_categories').update(payload).eq('id', initial.id)
      : await supabase.from('inventory_categories').insert({ ...payload, organization_id: orgId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }
  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('inv.editCategory') : t('inv.newCategory')}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <Field label={t('inv.code')} required error={err.code}><TextInput value={f.code} error={!!err.code} onChange={(e) => setF({ ...f, code: e.target.value })} autoFocus /></Field>
        <Field label={t('form.name')} required error={err.name}><TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Parametrable library: location (emplacement) ─────────────────────────────
export function LocationForm({ open, onClose, onSaved, initial }: BaseProps<LocationRow>) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [f, setF] = useState({ code: '', name: '', notes: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) return; setErr({}); setF({ code: initial?.code ?? '', name: initial?.name ?? '', notes: initial?.notes ?? '' }); }, [open, initial]);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.code.trim()) er.code = t('form.errRequired');
    if (!f.name.trim()) er.name = t('form.errRequired');
    setErr(er); if (Object.keys(er).length) return;
    setBusy(true);
    const payload = { code: f.code.trim(), name: f.name.trim(), notes: f.notes.trim() || null };
    const res = initial?.id
      ? await supabase.from('inventory_locations').update(payload).eq('id', initial.id)
      : await supabase.from('inventory_locations').insert({ ...payload, organization_id: orgId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }
  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('inv.editLocation') : t('inv.newLocation')}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('inv.code')} required error={err.code}><TextInput value={f.code} error={!!err.code} onChange={(e) => setF({ ...f, code: e.target.value })} autoFocus /></Field>
          <Field label={t('form.name')} required error={err.name}><TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        </div>
        <Field label={t('form.notes')}><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Tool register entry (durable equipment) ──────────────────────────────────
export function ToolForm({ open, onClose, onSaved, initial }: BaseProps<ToolRow>) {
  const { t } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const [cats, setCats] = useState<CategoryRow[]>([]);
  const [locs, setLocs] = useState<LocationRow[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [f, setF] = useState({
    name: '', serial_number: '', category_id: '', location_id: '', supplier_id: '',
    purchase_date: '', purchase_price: '', warranty_until: '', next_calibration_at: '', status: 'available' as ToolStatus, notes: '',
  });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return; setErr({});
    setF({
      name: initial?.name ?? '', serial_number: initial?.serial_number ?? '', category_id: initial?.category_id ?? '',
      location_id: initial?.location_id ?? '', supplier_id: initial?.supplier_id ?? '',
      purchase_date: initial?.purchase_date ?? '', purchase_price: initial?.purchase_price != null ? String(initial.purchase_price) : '',
      warranty_until: initial?.warranty_until ?? '', next_calibration_at: initial?.next_calibration_at ?? '',
      status: initial?.status ?? 'available', notes: initial?.notes ?? '',
    });
    supabase.from('inventory_categories').select('id,kind,code,name,parent_id').eq('kind', 'tool').is('deleted_at', null).order('name').then((r) => setCats((r.data as any) ?? []));
    supabase.from('inventory_locations').select('id,code,name,notes').is('deleted_at', null).order('name').then((r) => setLocs((r.data as any) ?? []));
    supabase.from('suppliers').select('id,name').is('deleted_at', null).order('name').then((r) => setSuppliers(r.data ?? []));
  }, [open, initial]);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.name.trim()) er.name = t('form.errRequired');
    if (!f.purchase_date) er.purchase_date = t('form.errRequired'); // mandatory per spec
    setErr(er); if (Object.keys(er).length) return;
    setBusy(true);
    const payload = {
      name: f.name.trim(), serial_number: f.serial_number.trim() || null, category_id: f.category_id || null,
      location_id: f.location_id || null, supplier_id: f.supplier_id || null, purchase_date: f.purchase_date,
      purchase_price: f.purchase_price.trim() === '' ? null : Number(f.purchase_price),
      warranty_until: f.warranty_until || null, next_calibration_at: f.next_calibration_at || null, status: f.status, notes: f.notes.trim() || null,
    };
    const res = initial?.id
      ? await supabase.from('tools').update(payload).eq('id', initial.id)
      : await supabase.from('tools').insert({ ...payload, organization_id: orgId, currency } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }
  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('tool.edit') : t('tool.new')} wide>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <Field label={t('form.name')} required error={err.name}><TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('tool.serial')}><TextInput value={f.serial_number} onChange={(e) => setF({ ...f, serial_number: e.target.value })} /></Field>
          <Field label={t('tool.status')}>
            <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value as ToolStatus })}>
              {TOOL_STATUSES.map((s) => <option key={s} value={s}>{t(`tool.status_${s}`)}</option>)}
            </Select>
          </Field>
          <Field label={t('inv.category')}>
            <Select value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })}>
              <option value="">{t('form.none')}</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label={t('inv.location')}>
            <Select value={f.location_id} onChange={(e) => setF({ ...f, location_id: e.target.value })}>
              <option value="">{t('form.none')}</option>
              {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          </Field>
          <Field label={t('tool.purchaseDate')} required error={err.purchase_date}><TextInput type="date" value={f.purchase_date} error={!!err.purchase_date} onChange={(e) => setF({ ...f, purchase_date: e.target.value })} /></Field>
          <Field label={t('tool.purchasePrice') + ` (${currency})`}><TextInput type="number" min="0" step="0.01" value={f.purchase_price} onChange={(e) => setF({ ...f, purchase_price: e.target.value })} /></Field>
          <Field label={t('part.supplier')}>
            <Select value={f.supplier_id} onChange={(e) => setF({ ...f, supplier_id: e.target.value })}>
              <option value="">{t('form.none')}</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
          <Field label={t('tool.warrantyUntil')}><TextInput type="date" value={f.warranty_until} onChange={(e) => setF({ ...f, warranty_until: e.target.value })} /></Field>
          <Field label={t('tool.nextCalibration')}><TextInput type="date" value={f.next_calibration_at} onChange={(e) => setF({ ...f, next_calibration_at: e.target.value })} /></Field>
        </div>
        <Field label={t('form.notes')}><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        {initial?.id && <PhotoStrip itemType="tool" itemId={initial.id} />}
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Reusable multi-photo strip (back-office + used by field via same bucket) ──
type Photo = { id: string; path: string; url?: string };
export function PhotoStrip({ itemType, itemId }: { itemType: 'part' | 'tool'; itemId: string }) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    const { data } = await supabase.from('inventory_photos').select('id,path').eq('item_type', itemType).eq('item_id', itemId).order('created_at');
    const rows = (data as Photo[]) ?? [];
    for (const p of rows) {
      const { data: s } = await supabase.storage.from('assets').createSignedUrl(p.path, 3600);
      p.url = s?.signedUrl;
    }
    setPhotos(rows);
  }
  useEffect(() => { if (itemId) void load(); /* eslint-disable-next-line */ }, [itemId]);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const path = `${orgId}/inventory/${itemType}/${itemId}/${crypto.randomUUID()}.jpg`;
    const up = await supabase.storage.from('assets').upload(path, file, { contentType: file.type || 'image/jpeg', upsert: false });
    if (!up.error) {
      await supabase.from('inventory_photos').insert({ organization_id: orgId, item_type: itemType, item_id: itemId, path } as any);
      await load();
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted">{t('inv.photos')}</span>
        <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? t('common.saving') : '+ ' + t('inv.addPhoto')}</Button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />
      </div>
      <div className="flex flex-wrap gap-2">
        {photos.map((p) => (
          <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="block h-20 w-20 overflow-hidden rounded-md border border-border bg-bg">
            {p.url && <img src={p.url} alt="" className="h-full w-full object-cover" />}
          </a>
        ))}
        {photos.length === 0 && <span className="text-2xs text-muted">{t('common.noData')}</span>}
      </div>
    </div>
  );
}
